import { IBenefits } from '@sapientpro/sapientpro-data-models';
import classnames from 'classnames';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useState } from 'react';
import { MouseParallax } from 'react-just-parallax';
import AnimatedTitle from '../../components/AnimatedTitle';
import Button, { ButtonVariants } from '../../components/Button';
import Ellipse from '../../components/Ellipse';
import clientDeviceSize, { ScreenSize } from '../../store/deviceScreenSize';
import isEducationModalOpen from '../../store/educationForm';
import styles from './EducationBenefits.module.scss';
import { EllipseVariants } from '../../../constants';

enum LearningTab {
  ACADEMY = 'academy',
  INTERNSHIP = 'internship',
}

type LearningTableProps = {
  data: IBenefits
};
const EducationBenefits = ({ data }: LearningTableProps) => {
  const deviceSize = useAtomValue<ScreenSize>(clientDeviceSize);
  const [activeTab, setActiveTab] = useState(LearningTab.ACADEMY);
  const setIsEducationModalOpen = useSetAtom(isEducationModalOpen);

  return (
    <section className={classnames(styles.benefits, 'container')}>
      <h2 className={classnames(styles.title, 'title3')}><AnimatedTitle title='Explore Our Learning **Opportunities**' /></h2>

      {deviceSize === ScreenSize.MOBILE ? (
        <>
          <div className={styles.tabs}>
            <button
              className={classnames(styles.tab, {
                [styles.active]: activeTab === LearningTab.ACADEMY,
              })}
              type='button'
              onClick={() => setActiveTab(LearningTab.ACADEMY)}
            >
              Academy
            </button>
            <button
              className={classnames(styles.tab, {
                [styles.active]: activeTab === LearningTab.INTERNSHIP,
              })}
              type='button'
              onClick={() => setActiveTab(LearningTab.INTERNSHIP)}
            >
              Internship
            </button>
          </div>
          <div className={styles.mobileContent}>
            <div className={styles.mobileItem}>
              <p className={styles.itemTitle}>Description</p>
              <p className='bodyText'>{data[`${activeTab}Description`]}</p>
            </div>
            <div className={styles.mobileItem}>
              <p className={styles.itemTitle}>Format</p>
              <p className='bodyText'>{data[`${activeTab}Format`]}</p>
            </div>
            <div className={styles.mobileItem}>
              <p className={styles.itemTitle}>Duration</p>
              <p className={styles.duration}>{data[`${activeTab}Duration`].title}</p>
              <p className='bodyText'>{data[`${activeTab}Duration`].filling}</p>
            </div>

          </div>
        </>
      ) : (
        <>
          <div className={styles.parallax}>
            <MouseParallax
              isAbsolutelyPositioned
              strength={0.05}
            >
              <Ellipse
                variant={EllipseVariants.DRAW}
                size={600}
                className={styles.parallax__draw}
              />
            </MouseParallax>
            <MouseParallax
              isAbsolutelyPositioned
              strength={0.12}
            >
              <Ellipse
                variant={EllipseVariants.NOISE}
                size={94}
                className={styles.parallax__noise}
              />
            </MouseParallax>
            <MouseParallax
              isAbsolutelyPositioned
              strength={0.08}
            >
              <Ellipse
                variant={EllipseVariants.GRADIENT}
                size={40}
                className={styles.parallax__gradient}
              />
            </MouseParallax>
          </div>
          <div
            className={styles.table}
            role='table'
            aria-label='Education variants'
          >
            <div
              className={styles.row}
              role='row'
            >
              <div role='cell' />
              <p
                role='cell'
                className={styles.tableHeader}
              >
                Academy
              </p>
              <p
                role='cell'
                className={styles.tableHeader}
              >
                Internship
              </p>
            </div>
            <div
              className={styles.row}
              role='row'
            >
              <p
                className={styles.rowTitle}
                role='cell'
              >
                Description
              </p>
              <div
                className={styles.column}
                role='cell'
              >
                <p className={classnames(styles.rowText, 'bodyText')}>{data.academyDescription}</p>
              </div>
              <div
                className={styles.column}
                role='cell'
              >
                <p className={classnames(styles.rowText, 'bodyText')}>{data.internshipDescription}</p>
              </div>
            </div>
            <div
              className={styles.row}
              role='row'
            >
              <p
                className={styles.rowTitle}
                role='cell'
              >
                Format:
              </p>
              <div
                className={styles.column}
                role='cell'
              >
                <p className={classnames(styles.rowText, 'bodyText')}>{data.academyFormat}</p>
              </div>
              <div
                className={styles.column}
                role='cell'
              >
                <p className={classnames(styles.rowText, 'bodyText')}>{data.internshipFormat}</p>
              </div>
            </div>
            <div
              className={styles.row}
              role='row'
            >
              <p
                className={styles.rowTitle}
                role='cell'
              >
                Duration:
              </p>
              <div
                className={styles.column}
                role='cell'
              >
                <p className={styles.duration}>{data.academyDuration.title}</p>
                <p className={classnames(styles.rowText, 'bodyText')}>{data.academyDuration.filling}</p>
              </div>
              <div
                className={styles.column}
                role='cell'
              >
                <p className={styles.duration}>{data.internshipDuration.title}</p>
                <p className={classnames(styles.rowText, 'bodyText')}>{data.internshipDuration.filling}</p>
              </div>
            </div>
          </div>
        </>
      )}
      <footer className={styles.footer}>
        <p className={classnames(styles.footerText, 'bodyText')}>{data.conclusion}</p>
        <Button
          variant={ButtonVariants.OUTLINED}
          className={styles.button}
          icon={(
            <svg>
              <use
                xlinkHref='/media/icon-nest.svg#iconNest'
                href='/media/icon-nest.svg#iconNest'
              />
            </svg>
          )}
          iconPosition='right'
          iconSize={{ width: 24, height: 24 }}
          onClick={() => setIsEducationModalOpen(true)}
        >
          ready to start
        </Button>
      </footer>
    </section>
  );
};

export default EducationBenefits;
